import style from "../style/SalaryPayment.module.scss";
import className from "classnames/bind";
import {Fragment, useState, useEffect, useCallback, useMemo} from "react";
import {Table, DatePicker, Select, InputNumber, Button, notification, Popconfirm} from "antd";
import dayjs from "dayjs";
import ActionBar from "~/layout/ActionBar";
import {Loading} from "~/components";
import {apiError, handleRequest, numberFormat, strToTime} from "~/utils";
import {useUtilities} from "~/hooks";
import {salaryPaymentApi} from "~/api";

const cn = className.bind(style);

const money = (value) => (value ? numberFormat(value) : '-');

const paymentOptions = [
	{value: 'cod', label: 'Tiền mặt'},
	{value: 'bank', label: 'Chuyển khoản'},
	{value: 'momo', label: 'Ví Momo'},
	{value: 'swipe', label: 'Pos'},
];

//Định dạng tiền cho InputNumber
const priceFormatter = (v) => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
const priceParser = (v) => (v || '').replace(/,/g, '');

function SalaryPayment() {

	const {utilities} = useUtilities();

	const [ky, setKy] = useState(dayjs().startOf('month'));

	const [loading, setLoading] = useState(false);

	const [employees, setEmployees] = useState([]);

	const [amounts, setAmounts] = useState({});            //{userId: số tiền chi}

	const [selectedKeys, setSelectedKeys] = useState([]);  //userId được chọn để chi

	const [payments, setPayments] = useState([]);          //các đợt đã chi

	const [submitting, setSubmitting] = useState(false);

	//Cấu hình đợt chi
	const [paidDate, setPaidDate] = useState(dayjs());
	const [paymentMethod, setPaymentMethod] = useState('bank');
	const [bankAccount, setBankAccount] = useState('');
	const [note, setNote] = useState('');

	//Công cụ lọc / chi nhanh: phòng ban (department) + phòng nhỏ (group/team)
	const [groupFilter, setGroupFilter] = useState('all');  //phòng ban
	const [teamFilter, setTeamFilter] = useState('all');    //phòng nhỏ trong phòng ban
	const [percent, setPercent] = useState(100);

	const bankAccountOptions = useMemo(() => (
		Object.entries(utilities.bankAccounts || {}).map(([key, value]) => ({value: key, label: value.title}))
	), [utilities.bankAccounts]);

	//Gom theo phòng ban (department): kỹ thuật, nhân sự, kế toán, kinh doanh...
	const groupOptions = useMemo(() => {
		const map = new Map();
		employees.forEach((e) => { if (!map.has(e.department)) map.set(e.department, e.departmentLabel); });
		return [{value: 'all', label: 'Tất cả phòng ban'}, ...Array.from(map, ([value, label]) => ({value, label}))];
	}, [employees]);

	const groupFilterLabel = useMemo(() => (
		groupOptions.find((o) => o.value === groupFilter)?.label || ''
	), [groupOptions, groupFilter]);

	//Phòng nhỏ (group/team) trong phòng ban đang chọn
	const teamOptions = useMemo(() => {
		const inDept = groupFilter === 'all' ? employees : employees.filter((e) => e.department === groupFilter);
		const names = Array.from(new Set(inDept.map((e) => e.groupName).filter(Boolean)));
		return [{value: 'all', label: 'Tất cả phòng nhỏ'}, ...names.map((n) => ({value: n, label: n}))];
	}, [employees, groupFilter]);

	//Điều kiện lọc chung: phòng ban + phòng nhỏ
	const matchFilter = useCallback((e) => (
		(groupFilter === 'all' || e.department === groupFilter) &&
		(teamFilter === 'all' || e.groupName === teamFilter)
	), [groupFilter, teamFilter]);

	//Danh sách hiển thị (lựa chọn & tổng tiền vẫn giữ theo toàn bộ)
	const displayedEmployees = useMemo(() => employees.filter(matchFilter), [employees, matchFilter]);

	//Đổi phòng ban -> reset phòng nhỏ
	const handleGroupFilterChange = (value) => { setGroupFilter(value); setTeamFilter('all'); };

	const load = useCallback(async (t) => {
		setLoading(true);
		const time = strToTime(t);
		const [[e1, r1], [e2, r2]] = await Promise.all([
			handleRequest(salaryPaymentApi.payable({time})),
			handleRequest(salaryPaymentApi.gets({time})),
		]);
		const msg = apiError('Lấy danh sách lương thất bại', e1, r1);
		if (!msg) {
			const list = r1.data?.employees || [];
			setEmployees(list);
			const initAmounts = {};
			const initSelected = [];
			list.forEach((e) => {
				initAmounts[e.userId] = e.remaining > 0 ? e.remaining : 0;
				if (e.remaining > 0) initSelected.push(e.userId);
			});
			setAmounts(initAmounts);
			setSelectedKeys(initSelected);
		}
		//Backend không bọc thêm khi payload đã có key 'data' -> r2.data chính là mảng
		if (!e2 && r2) setPayments(Array.isArray(r2.data) ? r2.data : (r2.data?.data || []));
		setLoading(false);
	}, []);

	useEffect(() => { load(ky).then(); }, [ky, load]);

	//Áp dụng % cho phòng đang chọn (cập nhật số tiền + tự chọn các dòng có tiền > 0)
	const applyPercent = () => {
		const pct = Math.max(0, Math.min(100, percent || 0));
		const next = {...amounts};
		const nextSelected = new Set(selectedKeys);
		employees.forEach((e) => {
			if (!matchFilter(e)) return;
			const value = Math.round((e.remaining > 0 ? e.remaining : 0) * pct / 100);
			next[e.userId] = value;
			if (value > 0) nextSelected.add(e.userId); else nextSelected.delete(e.userId);
		});
		setAmounts(next);
		setSelectedKeys(Array.from(nextSelected));
	};

	const setAmount = (userId, value) => setAmounts((a) => ({...a, [userId]: value || 0}));

	const totalSelected = useMemo(() => (
		selectedKeys.reduce((sum, uid) => sum + (Number(amounts[uid]) || 0), 0)
	), [selectedKeys, amounts]);

	const paymentsTotal = useMemo(() => payments.reduce((s, p) => s + (p.price || 0), 0), [payments]);

	const handleSubmit = async () => {
		if (!paidDate) { notification.error({message: 'Lỗi', description: 'Bạn chưa chọn ngày chi'}); return; }
		if (paymentMethod === 'bank' && !bankAccount) { notification.error({message: 'Lỗi', description: 'Bạn chưa chọn tài khoản ngân hàng'}); return; }
		const items = selectedKeys
			.map((uid) => ({userId: uid, price: Number(amounts[uid]) || 0}))
			.filter((i) => i.price > 0);
		if (!items.length) { notification.error({message: 'Lỗi', description: 'Chưa có khoản chi hợp lệ'}); return; }

		setSubmitting(true);
		const [error, response] = await handleRequest(salaryPaymentApi.add({
			time: strToTime(ky),
			paidDate: strToTime(paidDate),
			paymentMethod,
			bankAccount: paymentMethod === 'bank' ? bankAccount : '',
			note,
			items,
		}));
		const msg = apiError('Chi lương thất bại', error, response);
		if (!msg) {
			notification.success({message: 'Thành công', description: response.message || 'Đã ghi nhận chi lương'});
			setNote('');
			await load(ky);
		}
		setSubmitting(false);
	};

	const handleDelete = async (id) => {
		const [error, response] = await handleRequest(salaryPaymentApi.delete(id));
		const msg = apiError('Xoá đợt chi thất bại', error, response);
		if (!msg) {
			notification.success({message: 'Thành công', description: 'Đã xoá đợt chi lương'});
			await load(ky);
		}
	};

	const accountLabel = (p) => {
		const m = paymentOptions.find((o) => o.value === p.paymentMethod)?.label || p.paymentMethod;
		if (p.paymentMethod === 'bank' && p.bankAccount) {
			return utilities.bankAccounts?.[p.bankAccount]?.title || p.bankAccount;
		}
		return m;
	};

	const columns = [
		{title: 'Nhân viên', dataIndex: 'name', fixed: 'left', width: 180, render: (v, r) => (
			<div><div style={{fontWeight: 600}}>{v}</div>{r.username ? <div style={{fontSize: 11, color: '#94a3b8'}}>{r.username}</div> : null}</div>
		)},
		{title: 'Phòng ban', dataIndex: 'departmentLabel', width: 150},
		{title: 'Cần trả', dataIndex: 'target', width: 130, align: 'right', render: money},
		{title: 'Đã trả', dataIndex: 'paid', width: 120, align: 'right', render: (v) => <span style={{color: '#16a34a'}}>{money(v)}</span>},
		{title: 'Còn lại', dataIndex: 'remaining', width: 130, align: 'right', render: (v) => <b style={{color: v > 0 ? '#e11d48' : '#94a3b8'}}>{money(v)}</b>},
		{title: '%', width: 90, align: 'center', render: (_, r) => (
			<InputNumber
				min={0} max={100} size="small" style={{width: 70}}
				value={r.remaining > 0 ? Math.round((Number(amounts[r.userId]) || 0) / r.remaining * 100) : 0}
				onChange={(pct) => setAmount(r.userId, Math.round((r.remaining > 0 ? r.remaining : 0) * (pct || 0) / 100))}
			/>
		)},
		{title: 'Số tiền chi', width: 160, align: 'right', render: (_, r) => (
			<InputNumber
				min={0} size="small" style={{width: 140}}
				formatter={priceFormatter} parser={priceParser}
				value={amounts[r.userId]}
				onChange={(v) => setAmount(r.userId, v)}
			/>
		)},
	];

	const paymentColumns = [
		{title: 'Nhân viên', dataIndex: 'name', width: 180},
		{title: 'Ngày chi', dataIndex: 'paidDate', width: 120, render: (t) => t ? dayjs.unix(t).format('DD/MM/YYYY') : '-'},
		{title: 'Tài khoản', width: 200, render: (_, p) => accountLabel(p)},
		{title: 'Ghi chú', dataIndex: 'note'},
		{title: 'Số tiền', dataIndex: 'price', width: 140, align: 'right', render: money},
		{title: '', width: 60, align: 'center', render: (_, p) => (
			<Popconfirm title="Xoá đợt chi này?" okText="Xoá" cancelText="Huỷ" onConfirm={() => handleDelete(p.id)}>
				<Button danger size="small" type="text">Xoá</Button>
			</Popconfirm>
		)},
	];

	return (
		<Fragment>
			<ActionBar title={'Chi lương'}></ActionBar>
			<div className="container d-block">
				<div className={cn('page')}>

					{/* Cấu hình đợt chi */}
					<div className={cn('panel')}>
						<div className={cn('panelHead')}>Đợt chi lương</div>
						<div className={cn('panelBody')}>
							<div className={cn('toolbar')}>
								<div className={cn('field')}>
									<label>Kỳ lương</label>
									<DatePicker picker="month" allowClear={false} format="[Tháng] M / YYYY"
										value={ky} onChange={(d) => d && setKy(d.startOf('month'))} style={{width: 160}}/>
								</div>
								<div className={cn('field')}>
									<label>Ngày chi</label>
									<DatePicker allowClear={false} format="DD/MM/YYYY"
										value={paidDate} onChange={(d) => d && setPaidDate(d)} style={{width: 150}}/>
								</div>
								<div className={cn('field')}>
									<label>Phương thức</label>
									<Select options={paymentOptions} value={paymentMethod} onChange={setPaymentMethod} style={{width: 150}}/>
								</div>
								{paymentMethod === 'bank' && (
									<div className={cn('field')}>
										<label>Tài khoản</label>
										<Select options={bankAccountOptions} value={bankAccount || undefined}
											placeholder="Chọn tài khoản" onChange={setBankAccount} style={{width: 220}}/>
									</div>
								)}
							</div>

							<hr style={{margin: '14px 0', border: 0, borderTop: '1px dashed #e2e8f0'}}/>

							<div className={cn('toolbar')}>
								<div className={cn('field')}>
									<label>Phòng ban</label>
									<Select options={groupOptions} value={groupFilter} onChange={handleGroupFilterChange} style={{width: 200}}/>
								</div>
								<div className={cn('field')}>
									<label>Phòng nhỏ</label>
									<Select options={teamOptions} value={teamFilter} onChange={setTeamFilter} style={{width: 200}}
										disabled={teamOptions.length <= 1}/>
								</div>
								<div className={cn('field')}>
									<label>% chi trả (cho danh sách đang lọc)</label>
									<InputNumber min={0} max={100} value={percent} onChange={(v) => setPercent(v ?? 0)} style={{width: 200}}/>
								</div>
								<Button onClick={applyPercent}>Áp dụng %</Button>
							</div>
						</div>
					</div>

					{/* Bảng nhân viên */}
					<div className={cn('panel')}>
						<div className={cn('panelHead')}>
							Lương cần chi — {displayedEmployees.length}/{employees.length} nhân viên
							{groupFilter !== 'all' ? ` (${groupFilterLabel}${teamFilter !== 'all' ? ' / ' + teamFilter : ''})` : ''}
						</div>
						<div className={cn('panelBody')}>
							{loading ? <Loading/> : (
								<div className={cn('table')}>
									<Table
										rowKey="userId"
										size="small"
										bordered
										columns={columns}
										dataSource={displayedEmployees}
										pagination={false}
										scroll={{x: 'max-content', y: 460}}
										rowSelection={{
											selectedRowKeys: selectedKeys,
											onChange: (keys) => setSelectedKeys(keys),
											preserveSelectedRowKeys: true,
										}}
										locale={{emptyText: 'Không có bảng lương cho kỳ này'}}
									/>
									<div className={cn('totalBar')}>
										<div>
											<div className={cn('totalLabel')}>Tổng tiền sẽ chi đợt này ({selectedKeys.length} nhân viên)</div>
											<div className={cn('totalValue')}>{numberFormat(totalSelected)} <span style={{fontSize: 13, fontWeight: 400}}>VND</span></div>
										</div>
										<Button type="primary" size="large" loading={submitting} onClick={handleSubmit} disabled={!totalSelected}>
											Xác nhận chi lương
										</Button>
									</div>
								</div>
							)}
						</div>
					</div>

					{/* Đã chi kỳ này */}
					<div className={cn('panel')}>
						<div className={cn('panelHead')}>Đã chi kỳ này — tổng {money(paymentsTotal)} VND</div>
						<div className={cn('panelBody')}>
							<div className={cn('table')}>
								<Table
									rowKey="id"
									size="small"
									columns={paymentColumns}
									dataSource={payments}
									pagination={false}
									scroll={{x: 'max-content'}}
									locale={{emptyText: 'Chưa có đợt chi nào'}}
									summary={() => (
										<Table.Summary fixed>
											<Table.Summary.Row>
												<Table.Summary.Cell index={0} colSpan={4}><b>Tổng đã chi</b></Table.Summary.Cell>
												<Table.Summary.Cell index={1} align="right"><b>{money(paymentsTotal)}</b></Table.Summary.Cell>
												<Table.Summary.Cell index={2}/>
											</Table.Summary.Row>
										</Table.Summary>
									)}
								/>
							</div>
						</div>
					</div>

				</div>
			</div>
		</Fragment>
	);
}

export default SalaryPayment;
